import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AppWindow, Plus, Search, X, Edit, Trash2, Check, Clock, CheckCircle2, XCircle,
  Loader2, Settings, Scissors, Gauge, Sparkles, KeyRound, Calendar, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#C6A15B]/60 focus:outline-none";
const labelCls = "block text-xs font-medium text-slate-400 mb-1.5";
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const Skeleton = ({ className = '' }) => <div className={`animate-pulse bg-white/[0.05] rounded-lg ${className}`} />;
const todayStr = () => new Date().toISOString().split('T')[0];
const plusYear = () => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d.toISOString().split('T')[0]; };

const SHOP_TYPES = {
  auto_detailing: 'Auto Detailing', tint_shop: 'Tint Shop', body_shop: 'Body Shop',
  dealership: 'Dealership', mobile_service: 'Mobile Service', other: 'Other',
};
const shopLabel = (t) => SHOP_TYPES[t] || t || '—';

const ST = {
  pending: { label: 'Pending Review', color: '#F59E0B', icon: Clock },
  approved: { label: 'Active', color: '#10B981', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: '#EF4444', icon: XCircle },
};
const stOf = (s) => ST[(s || '').toLowerCase()] || { label: s || 'Unknown', color: '#64748B', icon: Clock };

const StatusPill = ({ status }) => {
  const m = stOf(status);
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full"
      style={{ background: `${m.color}1f`, color: m.color }} data-testid="sw-status-pill">
      <m.icon className="w-3 h-3" />{m.label}
    </span>
  );
};

const PlanBadge = ({ plan }) => {
  const map = { Basic: '#3B82F6', Pro: '#8B5CF6', Enterprise: '#C6A15B' };
  const c = map[plan] || '#64748B';
  return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md" style={{ background: `${c}22`, color: c }}>{plan || '—'}</span>;
};

/* ============================= ADMIN WORKSPACE ============================= */

const SummaryCard = ({ label, value, color, icon: Icon }) => (
  <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-5 flex items-center justify-between">
    <div>
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      <p className="text-2xl font-bold" style={{ color }}>{value}</p>
    </div>
    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}1f` }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
  </div>
);

const AccountRow = ({ req, onApprove, onReject, onEdit, onDelete }) => {
  const approved = req.status === 'approved';
  return (
    <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-5" data-testid={`sw-account-${req.request_id}`}>
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap mb-1">
            <h4 className="text-base font-semibold text-slate-100 truncate">{req.business_name}</h4>
            <StatusPill status={req.status} />
            {approved && <PlanBadge plan={req.plan_type} />}
          </div>
          <p className="text-sm text-slate-400">{req.contact_person} · {req.email}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {shopLabel(req.shop_type)}{req.monthly_volume ? ` · ${req.monthly_volume} vehicles/mo` : ''} · Requested {fmtDate(req.created_at)}
          </p>

          {approved && (
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              <div className="bg-black/40 border border-emerald-500/20 rounded-lg px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Credentials</p>
                <p className="text-sm text-emerald-300 font-mono">{req.software_username} / {req.software_password}</p>
              </div>
              <div className="bg-black/40 border border-white/10 rounded-lg px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Valid Until</p>
                <p className="text-sm text-slate-200">{req.subscription_end ? fmtDate(req.subscription_end) : 'No end date'}</p>
              </div>
            </div>
          )}

          {req.admin_notes && (
            <p className="mt-2 text-xs text-slate-400"><span className="text-slate-500">Notes: </span>{req.admin_notes}</p>
          )}
        </div>

        <div className="flex flex-wrap lg:flex-col gap-2 lg:w-40 lg:items-stretch">
          {req.status === 'pending' && (
            <>
              <button onClick={() => onApprove(req)} className="inline-flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors" data-testid={`sw-approve-${req.request_id}`}>
                <Check className="w-3.5 h-3.5" /> Approve
              </button>
              <button onClick={() => onReject(req)} className="inline-flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-red-600/90 hover:bg-red-500 text-white transition-colors" data-testid={`sw-reject-${req.request_id}`}>
                <X className="w-3.5 h-3.5" /> Reject
              </button>
            </>
          )}
          {approved && (
            <button onClick={() => onEdit(req)} className="inline-flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 transition-colors" data-testid={`sw-edit-${req.request_id}`}>
              <Edit className="w-3.5 h-3.5" /> Edit
            </button>
          )}
          <button onClick={() => onDelete(req)} className="inline-flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-transparent hover:bg-red-500/10 border border-red-500/40 text-red-400 transition-colors" data-testid={`delete-software-btn-${req.request_id}`}>
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const Modal = ({ open, onClose, title, subtitle, icon: Icon, children, testId, maxW = 'max-w-lg' }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className={`bg-[#101317] border border-white/10 rounded-2xl w-full ${maxW} max-h-[88vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-start justify-between p-5 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            {Icon && <div className="w-9 h-9 rounded-xl bg-[#C6A15B]/15 flex items-center justify-center"><Icon className="w-5 h-5 text-[#C6A15B]" /></div>}
            <div>
              <h3 className="text-base font-semibold text-slate-100">{title}</h3>
              {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

const AdminSoftware = () => {
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [renewal, setRenewal] = useState(null);
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [reviewMode, setReviewMode] = useState('approve');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyApprove = { plan_type: 'Basic', username: '', password: '', subscription_start: todayStr(), subscription_end: plusYear(), admin_notes: '' };
  const [reviewForm, setReviewForm] = useState(emptyApprove);
  const [editForm, setEditForm] = useState({ plan_type: '', username: '', password: '', subscription_start: '', subscription_end: '', admin_notes: '', status: '' });
  const emptyCreate = { business_name: '', contact_person: '', email: '', phone: '', shop_type: 'body_shop', plan_type: 'Basic', username: '', password: '', subscription_start: todayStr(), subscription_end: plusYear(), admin_notes: '' };
  const [createForm, setCreateForm] = useState(emptyCreate);

  const RENEWAL_KEY = { active: 'active', expiring: 'expiring_soon', expired: 'expired' };

  const fetchRequests = async (t) => {
    setLoading(true);
    try {
      const lifecycle = ['active', 'expiring', 'expired'].includes(t);
      const status = lifecycle ? 'approved' : (t === 'all' ? '' : t);
      const res = await axios.get(`${API}/api/admin/software-requests${status ? `?status=${status}` : ''}`);
      setRequests(res.data.requests || []);
      setCounts(res.data.status_counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (e) { toast.error('Failed to load software accounts'); }
    finally { setLoading(false); }
  };
  const fetchRenewal = async () => {
    try { const res = await axios.get(`${API}/api/admin/software-accounts/renewal-status`); setRenewal(res.data); }
    catch { /* non-blocking */ }
  };

  useEffect(() => { fetchRenewal(); }, []);
  useEffect(() => { fetchRequests(tab); }, [tab]);

  const runSearch = async (e) => {
    e.preventDefault();
    if (!query.trim() || query.trim().length < 2) { toast.error('Enter at least 2 characters'); return; }
    setSearching(true);
    try { const res = await axios.get(`${API}/api/admin/software-requests/search?q=${encodeURIComponent(query.trim())}`); setSearchResults(res.data); }
    catch { toast.error('Search failed'); setSearchResults([]); }
    finally { setSearching(false); }
  };
  const clearSearch = () => { setQuery(''); setSearchResults(null); };

  const openApprove = (req) => { setSelected(req); setReviewMode('approve'); setReviewForm(emptyApprove); setShowReview(true); };
  const openReject = (req) => { setSelected(req); setReviewMode('reject'); setReviewForm({ ...emptyApprove, admin_notes: '' }); setShowReview(true); };
  const openEdit = (req) => {
    setSelected(req);
    setEditForm({
      plan_type: req.plan_type || 'Basic', username: req.software_username || '', password: req.software_password || '',
      subscription_start: req.subscription_start?.split('T')[0] || '', subscription_end: req.subscription_end?.split('T')[0] || '',
      admin_notes: req.admin_notes || '', status: req.status || 'approved',
    });
    setShowEdit(true);
  };

  const submitReview = async () => {
    if (!selected) return;
    if (reviewMode === 'approve' && (!reviewForm.username || !reviewForm.password)) { toast.error('Username and password are required'); return; }
    if (reviewMode === 'reject' && !reviewForm.admin_notes.trim()) { toast.error('Please provide a rejection reason'); return; }
    setSaving(true);
    try {
      const payload = { action: reviewMode, admin_notes: reviewForm.admin_notes };
      if (reviewMode === 'approve') {
        payload.plan_type = reviewForm.plan_type; payload.username = reviewForm.username; payload.password = reviewForm.password;
        payload.subscription_start = reviewForm.subscription_start; payload.subscription_end = reviewForm.subscription_end;
      }
      await axios.put(`${API}/api/admin/software-requests/${selected.request_id}/approve`, payload);
      toast.success(reviewMode === 'approve' ? 'Access approved' : 'Request rejected');
      setShowReview(false); fetchRequests(tab); fetchRenewal();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setSaving(false); }
  };

  const submitEdit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/software-requests/${selected.request_id}`, {
        plan_type: editForm.plan_type, username: editForm.username, password: editForm.password,
        subscription_start: editForm.subscription_start, subscription_end: editForm.subscription_end,
        admin_notes: editForm.admin_notes, status: editForm.status,
      });
      toast.success('Subscription updated');
      setShowEdit(false); fetchRequests(tab); fetchRenewal();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.subscription_end) { toast.error('Subscription end date is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/software-accounts`, {
        ...createForm,
        subscription_start: new Date(createForm.subscription_start).toISOString(),
        subscription_end: new Date(createForm.subscription_end).toISOString(),
      });
      toast.success('Software account created');
      setShowCreate(false); setCreateForm(emptyCreate); fetchRequests(tab); fetchRenewal();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create account'); }
    finally { setSaving(false); }
  };

  const del = async (req) => {
    if (!window.confirm('Delete this software account/request? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/admin/software-requests/${req.request_id}`);
      toast.success('Deleted');
      fetchRequests(tab); fetchRenewal();
      if (searchResults) setSearchResults(searchResults.filter(r => r.request_id !== req.request_id));
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const activeCount = renewal ? (renewal.counts.active + renewal.counts.no_end_date) : 0;
  const expiringCount = renewal ? renewal.counts.expiring_soon : 0;
  const expiredCount = renewal ? renewal.counts.expired : 0;

  const TABS = [
    { k: 'pending', label: 'Pending', n: counts.pending, c: '#F59E0B' },
    { k: 'active', label: 'Active', n: activeCount, c: '#10B981' },
    { k: 'expiring', label: 'Expiring Soon', n: expiringCount, c: '#FBBF24' },
    { k: 'expired', label: 'Expired', n: expiredCount, c: '#EF4444' },
    { k: 'rejected', label: 'Rejected', n: counts.rejected, c: '#EF4444' },
    { k: 'all', label: 'All', n: null, c: '#C6A15B' },
  ];

  const displayList = useMemo(() => {
    if (searchResults) return searchResults;
    if (['active', 'expiring', 'expired'].includes(tab) && renewal) {
      let list = renewal[RENEWAL_KEY[tab]] || [];
      if (tab === 'active') list = [...list, ...(renewal.no_end_date || [])];
      return list;
    }
    return requests;
  }, [searchResults, tab, renewal, requests]);

  return (
    <div className="space-y-6" data-testid="admin-software-section">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-2.5">
            <AppWindow className="w-6 h-6 text-[#C6A15B]" /> TC CutPro Software
          </h2>
          <p className="text-sm text-slate-400 mt-1">Manage software access requests and subscriptions.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#C6A15B] hover:bg-[#b5924f] text-black text-sm font-semibold transition-colors self-start" data-testid="create-software-account-btn">
          <Plus className="w-4 h-4" /> New Account
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard label="Pending" value={counts.pending} color="#F59E0B" icon={Clock} />
        <SummaryCard label="Active" value={activeCount} color="#10B981" icon={CheckCircle2} />
        <SummaryCard label="Expiring Soon" value={expiringCount} color="#FBBF24" icon={Calendar} />
        <SummaryCard label="Expired" value={expiredCount} color="#EF4444" icon={XCircle} />
      </div>

      {/* Search */}
      <form onSubmit={runSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input className={`${inputCls} pl-9`} placeholder="Search by business, contact, email, company or username…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="software-search-input" />
        </div>
        <button type="submit" disabled={searching} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-slate-200">{searching ? 'Searching…' : 'Search'}</button>
        {searchResults !== null && <button type="button" onClick={clearSearch} className="px-4 py-2 rounded-lg bg-transparent border border-white/10 text-sm text-slate-400">Clear</button>}
      </form>

      {/* Tabs */}
      {!searchResults && (
        <div className="flex gap-1 flex-wrap border-b border-white/[0.06]" data-testid="software-status-tabs">
          {TABS.map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.k ? 'border-current' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
              style={tab === t.k ? { color: t.c } : {}} data-testid={`tab-${t.k}`}>
              {t.label}{t.n !== null ? ` (${t.n})` : ''}
            </button>
          ))}
        </div>
      )}

      {/* List */}
      <div className="space-y-3" data-testid="software-accounts-list">
        {loading ? (
          <>{[1, 2, 3].map(i => <Skeleton key={i} className="h-28 w-full" />)}</>
        ) : displayList.length === 0 ? (
          <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl py-16 text-center">
            <AppWindow className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-300 font-medium">No accounts in this view</p>
            <p className="text-slate-500 text-sm mt-1">{searchResults ? 'Try a different search.' : 'Accounts will appear here.'}</p>
          </div>
        ) : (
          displayList.map(req => <AccountRow key={req.request_id} req={req} onApprove={openApprove} onReject={openReject} onEdit={openEdit} onDelete={del} />)
        )}
      </div>

      {/* Create account modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Software Account" subtitle="Skips the request/approval flow — instantly active." icon={Plus} testId="create-software-account-dialog" maxW="max-w-2xl">
        <form onSubmit={submitCreate} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className={labelCls}>Business Name *</label><input required className={inputCls} value={createForm.business_name} onChange={(e) => setCreateForm({ ...createForm, business_name: e.target.value })} data-testid="create-sw-business" /></div>
            <div><label className={labelCls}>Contact Person *</label><input required className={inputCls} value={createForm.contact_person} onChange={(e) => setCreateForm({ ...createForm, contact_person: e.target.value })} data-testid="create-sw-contact" /></div>
            <div><label className={labelCls}>Email *</label><input required type="email" className={inputCls} value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} data-testid="create-sw-email" /></div>
            <div><label className={labelCls}>Phone</label><input className={inputCls} value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} /></div>
            <div><label className={labelCls}>Plan *</label>
              <select className={inputCls} value={createForm.plan_type} onChange={(e) => setCreateForm({ ...createForm, plan_type: e.target.value })} data-testid="create-sw-plan">
                <option value="Basic">Basic</option><option value="Pro">Pro</option><option value="Enterprise">Enterprise</option>
              </select>
            </div>
            <div><label className={labelCls}>Shop Type</label>
              <select className={inputCls} value={createForm.shop_type} onChange={(e) => setCreateForm({ ...createForm, shop_type: e.target.value })}>
                {Object.entries(SHOP_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Software Username *</label><input required className={inputCls} value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })} data-testid="create-sw-username" /></div>
            <div><label className={labelCls}>Software Password *</label><input required className={inputCls} value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} data-testid="create-sw-password" /></div>
            <div><label className={labelCls}>Subscription Start</label><input type="date" className={inputCls} value={createForm.subscription_start} onChange={(e) => setCreateForm({ ...createForm, subscription_start: e.target.value })} /></div>
            <div><label className={labelCls}>Subscription End *</label><input required type="date" className={inputCls} value={createForm.subscription_end} onChange={(e) => setCreateForm({ ...createForm, subscription_end: e.target.value })} data-testid="create-sw-end" /></div>
          </div>
          <div><label className={labelCls}>Admin Notes</label><textarea rows={2} className={inputCls} value={createForm.admin_notes} onChange={(e) => setCreateForm({ ...createForm, admin_notes: e.target.value })} /></div>
          <div className="flex justify-end gap-2 pt-2 border-t border-white/[0.06]">
            <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg border border-white/10 text-sm text-slate-300">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#C6A15B] hover:bg-[#b5924f] text-black text-sm font-semibold" data-testid="create-sw-submit-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Account'}</button>
          </div>
        </form>
      </Modal>

      {/* Review (approve/reject) modal */}
      <Modal open={showReview} onClose={() => setShowReview(false)} title={reviewMode === 'approve' ? 'Approve Access' : 'Reject Request'} subtitle={reviewMode === 'approve' ? 'Configure subscription details' : 'Provide a reason for rejection'} icon={reviewMode === 'approve' ? CheckCircle2 : XCircle} testId="sw-review-dialog">
        {selected && (
          <div className="space-y-4">
            <div className="bg-black/40 border border-white/10 rounded-lg p-3">
              <p className="text-sm font-semibold text-slate-100">{selected.business_name}</p>
              <p className="text-xs text-slate-400">{selected.contact_person} · {selected.email} · {shopLabel(selected.shop_type)}</p>
            </div>
            {reviewMode === 'approve' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Plan</label>
                    <select className={inputCls} value={reviewForm.plan_type} onChange={(e) => setReviewForm({ ...reviewForm, plan_type: e.target.value })}>
                      <option value="Basic">Basic</option><option value="Pro">Pro</option><option value="Enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div><label className={labelCls}>Username *</label><input className={inputCls} value={reviewForm.username} onChange={(e) => setReviewForm({ ...reviewForm, username: e.target.value })} data-testid="sw-approve-username" /></div>
                </div>
                <div><label className={labelCls}>Password *</label><input className={inputCls} value={reviewForm.password} onChange={(e) => setReviewForm({ ...reviewForm, password: e.target.value })} data-testid="sw-approve-password" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Subscription Start</label><input type="date" className={inputCls} value={reviewForm.subscription_start} onChange={(e) => setReviewForm({ ...reviewForm, subscription_start: e.target.value })} /></div>
                  <div><label className={labelCls}>Subscription End</label><input type="date" className={inputCls} value={reviewForm.subscription_end} onChange={(e) => setReviewForm({ ...reviewForm, subscription_end: e.target.value })} /></div>
                </div>
              </>
            )}
            <div><label className={labelCls}>{reviewMode === 'approve' ? 'Notes (optional)' : 'Rejection Reason *'}</label>
              <textarea rows={3} className={inputCls} value={reviewForm.admin_notes} onChange={(e) => setReviewForm({ ...reviewForm, admin_notes: e.target.value })} data-testid="sw-review-notes" /></div>
            <div className="flex justify-end gap-2 pt-2 border-t border-white/[0.06]">
              <button onClick={() => setShowReview(false)} className="px-4 py-2 rounded-lg border border-white/10 text-sm text-slate-300">Cancel</button>
              <button onClick={submitReview} disabled={saving} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white ${reviewMode === 'approve' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-red-600 hover:bg-red-500'}`} data-testid="sw-review-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (reviewMode === 'approve' ? 'Approve' : 'Reject')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit modal */}
      <Modal open={showEdit} onClose={() => setShowEdit(false)} title="Edit Subscription" subtitle={selected?.business_name} icon={Edit} testId="sw-edit-dialog">
        {selected && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Plan</label>
                <select className={inputCls} value={editForm.plan_type} onChange={(e) => setEditForm({ ...editForm, plan_type: e.target.value })}>
                  <option value="Basic">Basic</option><option value="Pro">Pro</option><option value="Enterprise">Enterprise</option>
                </select>
              </div>
              <div><label className={labelCls}>Status</label>
                <select className={inputCls} value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                  <option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Username</label><input className={inputCls} value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} /></div>
              <div><label className={labelCls}>Password</label><input className={inputCls} value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Start</label><input type="date" className={inputCls} value={editForm.subscription_start} onChange={(e) => setEditForm({ ...editForm, subscription_start: e.target.value })} /></div>
              <div><label className={labelCls}>End</label><input type="date" className={inputCls} value={editForm.subscription_end} onChange={(e) => setEditForm({ ...editForm, subscription_end: e.target.value })} /></div>
            </div>
            <div><label className={labelCls}>Notes</label><textarea rows={3} className={inputCls} value={editForm.admin_notes} onChange={(e) => setEditForm({ ...editForm, admin_notes: e.target.value })} /></div>
            <div className="flex justify-end gap-2 pt-2 border-t border-white/[0.06]">
              <button onClick={() => setShowEdit(false)} className="px-4 py-2 rounded-lg border border-white/10 text-sm text-slate-300">Cancel</button>
              <button onClick={submitEdit} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#C6A15B] hover:bg-[#b5924f] text-black text-sm font-semibold">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

/* ============================= CUSTOMER / DEALER VIEW ============================= */

const FeatureCard = ({ icon: Icon, title, desc, color }) => (
  <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-6">
    <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: `${color}1f` }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <h3 className="text-base font-semibold text-slate-100 mb-1.5">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
  </div>
);

const CustomerSoftware = ({ user }) => {
  const [myRequest, setMyRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    business_name: user?.company_name || '', contact_person: user?.full_name || '', email: user?.email || '',
    phone: user?.phone || '', shop_type: '', monthly_volume: '', current_software: '', additional_info: '',
  });

  const fetchMine = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/api/software-requests/my-request`); setMyRequest(res.data); }
    catch { setMyRequest(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchMine(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/software-requests`, form);
      toast.success('Request submitted! Our team will review it shortly.');
      setShowForm(false); fetchMine();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit request'); }
    finally { setSubmitting(false); }
  };

  const status = myRequest?.status;
  const canRequest = !myRequest;

  return (
    <div className="space-y-6" data-testid="dealer-software-section">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-[#C6A15B]/20 bg-gradient-to-br from-[#12151A] via-[#0E1115] to-black p-8 md:p-10">
        <div className="relative flex items-start gap-5">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#C6A15B] to-[#8a6f36] flex items-center justify-center flex-shrink-0">
            <Scissors className="w-7 h-7 text-black" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">TC CutPro Software</h1>
            <p className="text-[#C6A15B] text-sm md:text-base mt-1">Precision cutting patterns for your installation workflow.</p>
            <p className="text-slate-400 text-sm mt-2 max-w-xl">Access the complete pattern database and cut every film job with laser-accurate templates — built for professional PPF & tint shops.</p>
          </div>
        </div>
      </div>

      {/* Current status */}
      {loading ? (
        <Skeleton className="h-32 w-full" />
      ) : myRequest ? (
        <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-6" data-testid="sw-current-status">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <h3 className="text-lg font-semibold text-slate-100">Your Software Status</h3>
            <StatusPill status={status} />
          </div>

          {status === 'pending' && (
            <div className="flex items-start gap-3 bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
              <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-300">Your request is under review. We'll notify you once it's processed.</p>
            </div>
          )}

          {status === 'approved' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
                <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-300">Your access is active. Use the credentials below to sign in to TC CutPro.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-black/40 border border-white/10 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1.5"><KeyRound className="w-3 h-3" /> Username</p>
                  <p className="text-emerald-300 font-mono font-semibold text-lg">{myRequest.software_username}</p>
                </div>
                <div className="bg-black/40 border border-white/10 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1.5"><KeyRound className="w-3 h-3" /> Password</p>
                  <p className="text-emerald-300 font-mono font-semibold text-lg">{myRequest.software_password}</p>
                </div>
                <div className="bg-black/40 border border-white/10 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Plan</p>
                  <p className="text-slate-100 font-semibold">{myRequest.plan_type || '—'}</p>
                </div>
                <div className="bg-black/40 border border-white/10 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Valid Until</p>
                  <p className="text-slate-100 font-semibold">{myRequest.subscription_end ? fmtDate(myRequest.subscription_end) : 'No end date'}</p>
                </div>
              </div>
              {myRequest.admin_notes && (
                <div className="bg-black/40 border border-white/10 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-0.5">Message from TC</p>
                  <p className="text-sm text-slate-300">{myRequest.admin_notes}</p>
                </div>
              )}
            </div>
          )}

          {status === 'rejected' && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-red-500/5 border border-red-500/20 rounded-lg p-4">
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-300">Your request was not approved.</p>
              </div>
              {myRequest.admin_notes && (
                <div className="bg-black/40 border border-white/10 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-0.5">Reason</p>
                  <p className="text-sm text-slate-300">{myRequest.admin_notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}

      {/* Features */}
      <div className="grid md:grid-cols-3 gap-4">
        <FeatureCard icon={Sparkles} color="#10B981" title="10,000+ Patterns" desc="A complete, constantly-updated database of vehicle pattern files for every make and model." />
        <FeatureCard icon={Gauge} color="#3B82F6" title="Laser Precision" desc="Cut film with pinpoint accuracy and eliminate waste on every installation." />
        <FeatureCard icon={Settings} color="#8B5CF6" title="Easy to Use" desc="Intuitive controls your whole team can master in minutes, not days." />
      </div>

      {/* CTA */}
      {canRequest && (
        <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-8 text-center">
          <h2 className="text-xl font-semibold text-slate-100 mb-2">Ready to cut smarter?</h2>
          <p className="text-slate-400 text-sm mb-6 max-w-lg mx-auto">Request access to TC CutPro and join the shops cutting with precision every day.</p>
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#C6A15B] hover:bg-[#b5924f] text-black text-sm font-semibold" data-testid="request-access-btn">
            <Scissors className="w-4 h-4" /> Request Access
          </button>
        </div>
      )}

      {/* Request form modal */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title="Request TC CutPro Access" subtitle="Tell us about your shop and we'll be in touch." icon={Scissors} testId="sw-request-dialog" maxW="max-w-2xl">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className={labelCls}>Business Name *</label><input required className={inputCls} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="req-business" /></div>
            <div><label className={labelCls}>Contact Person *</label><input required className={inputCls} value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="req-contact" /></div>
            <div><label className={labelCls}>Email *</label><input required type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="req-email" /></div>
            <div><label className={labelCls}>Phone *</label><input required className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="req-phone" /></div>
            <div><label className={labelCls}>Shop Type *</label>
              <select required className={inputCls} value={form.shop_type} onChange={(e) => setForm({ ...form, shop_type: e.target.value })} data-testid="req-shop-type">
                <option value="">Select…</option>
                {Object.entries(SHOP_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Monthly Volume *</label>
              <select required className={inputCls} value={form.monthly_volume} onChange={(e) => setForm({ ...form, monthly_volume: e.target.value })} data-testid="req-volume">
                <option value="">Select…</option>
                <option value="1-10">1-10 vehicles</option><option value="11-25">11-25 vehicles</option>
                <option value="26-50">26-50 vehicles</option><option value="51-100">51-100 vehicles</option><option value="100+">100+ vehicles</option>
              </select>
            </div>
          </div>
          <div><label className={labelCls}>Current Software</label><input className={inputCls} value={form.current_software} onChange={(e) => setForm({ ...form, current_software: e.target.value })} placeholder="What are you using today? (optional)" /></div>
          <div><label className={labelCls}>Additional Info</label><textarea rows={3} className={inputCls} value={form.additional_info} onChange={(e) => setForm({ ...form, additional_info: e.target.value })} placeholder="Tell us about your needs…" /></div>
          <div className="flex justify-end gap-2 pt-2 border-t border-white/[0.06]">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg border border-white/10 text-sm text-slate-300">Cancel</button>
            <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#C6A15B] hover:bg-[#b5924f] text-black text-sm font-semibold" data-testid="req-submit">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Request'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export const SoftwareCenter = ({ user }) => {
  return user?.role === 'tc_admin' ? <AdminSoftware /> : <CustomerSoftware user={user} />;
};

export default SoftwareCenter;
